import { useState, memo } from 'react';
import { useTranslation } from 'react-i18next';

import { MultiSelect } from '../../../components/common/MultiSelect/MultiSelect';
import { useStationsListQuery } from '../../../hooks/useDashboardQueries';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import { StationOption } from '../../../services/ocpp/types';

const SEARCH_DEBOUNCE_MS = 300;

interface StationMultiSelectProps {
  selectedStations: StationOption[];
  onChange: (stations: StationOption[]) => void;
  organizationIds?: string;
}

const getStationLabel = (station: StationOption) => station.address || station.idTag;

export const StationMultiSelect = memo(function StationMultiSelect({
  selectedStations,
  onChange,
  organizationIds,
}: StationMultiSelectProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);

  const { data: options = [], isLoading: loading } = useStationsListQuery({
    search: debouncedSearch,
    organizationIds,
    enabled: isOpen,
  });

  return (
    <MultiSelect
      isOpen={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      search={search}
      onSearchChange={setSearch}
      options={options}
      selected={selectedStations}
      onChange={onChange}
      loading={loading}
      getId={(station) => station.id}
      getLabel={getStationLabel}
      getSublabel={(station) => station.idTag}
      icon="ev_station"
      showChips
      focusSearchOnOpen
      getChipRemoveAria={(station) =>
        t('dashboard.metrics.removeStationAria', { name: getStationLabel(station) })
      }
      labels={{
        toggle:
          selectedStations.length === 0
            ? t('dashboard.metrics.allStations')
            : t('dashboard.metrics.stationsCount', { count: selectedStations.length }),
        toggleAria: t('dashboard.metrics.selectStationsAria'),
        searchPlaceholder: t('dashboard.metrics.searchStationsPlaceholder'),
        searchAria: t('dashboard.metrics.searchStationsAria'),
        loading: t('dashboard.metrics.loading'),
        empty: t('dashboard.metrics.noStationsFound'),
        clearAll: t('dashboard.metrics.clearAll'),
      }}
    />
  );
});

export default StationMultiSelect;
